import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, Switch, Space,
  Typography, Row, Col, Statistic, Card, Tooltip, message,
  List, Badge, Select, Popconfirm,
} from 'antd';
import {
  QrcodeOutlined, ReloadOutlined, EyeOutlined, EditOutlined,
  DownloadOutlined, PrinterOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { qrAPI } from '../services/api';
import { brand, gray, state, modalWidth } from '../theme/tokens';
import { EmptyState } from '../components/common';

const { Text, Title } = Typography;

const TARGET_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  schedule:  { label: '시험일정', color: 'blue' },
  protocol:  { label: '시험계획서', color: 'purple' },
  treatment: { label: '약제처리', color: 'orange' },
  partner:   { label: '파트너', color: 'green' },
};

interface QRItem {
  id: number;
  short_code: string;
  target_type: string;
  target_id: number;
  redirect_url: string;
  description: string;
  is_active: boolean;
  scan_count: number;
  created_at: string;
  updated_at: string;
  qr_url: string;
  image_url: string;
}

interface Stats {
  total: number;
  active: number;
  total_scans: number;
  recent_scans_7d: number;
  by_type: { target_type: string; count: number; scans: number }[];
  top_qrs: { short_code: string; target_type: string; description: string; scan_count: number }[];
}

export default function QRManagementPage() {
  const [items, setItems] = useState<QRItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filterType, setFilterType] = useState<string | undefined>();

  const [editItem, setEditItem] = useState<QRItem | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [editForm] = Form.useForm();

  const [logItem, setLogItem] = useState<QRItem | null>(null);
  const [logVisible, setLogVisible] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [logTotal, setLogTotal] = useState(0);

  const [imageItem, setImageItem] = useState<QRItem | null>(null);
  const [imageVisible, setImageVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qrAPI.list({ target_type: filterType, page, page_size: 20 });
      setItems(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [filterType, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await qrAPI.stats();
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openEdit = (item: QRItem) => {
    setEditItem(item);
    editForm.setFieldsValue({
      redirect_url: item.redirect_url,
      description: item.description,
      is_active: item.is_active,
    });
    setEditVisible(true);
  };

  const handleEditSave = async () => {
    const vals = await editForm.validateFields();
    await qrAPI.updateRedirect(editItem!.short_code, vals);
    message.success('저장되었습니다.');
    setEditVisible(false);
    fetchData();
    fetchStats();
  };

  const openLogs = async (item: QRItem) => {
    setLogItem(item);
    setLogVisible(true);
    const res = await qrAPI.logs(item.short_code, { page: 1, page_size: 50 });
    setLogs(res.data.logs);
    setLogTotal(res.data.total);
  };

  const openImage = (item: QRItem) => {
    setImageItem(item);
    setImageVisible(true);
  };

  const handleDownload = async (item: QRItem) => {
    const a = document.createElement('a');
    a.href = item.image_url;
    a.download = `qr_${item.short_code}.png`;
    a.click();
  };

  const handlePrint = (item: QRItem) => {
    const win = window.open('', '_blank', 'width=400,height=500');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html><html><head>
      <title>QR 포장 표지판</title>
      <style>
        body { font-family: 'Pretendard', sans-serif; display:flex; flex-direction:column;
               align-items:center; justify-content:center; min-height:100vh; margin:0; }
        .label { border:2px solid #1e293b; border-radius:12px; padding:24px 32px;
                 text-align:center; width:280px; }
        .type { font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:1px; margin-bottom:6px; }
        .desc { font-size:16px; font-weight:700; color:#1e293b; margin-bottom:16px; }
        img { width:160px; height:160px; }
        .code { font-size:11px; color:#94a3b8; margin-top:8px; font-family:monospace; }
        @media print { body { margin:0; } }
      </style></head><body>
      <div class="label">
        <div class="type">${TARGET_TYPE_LABELS[item.target_type]?.label || item.target_type}</div>
        <div class="desc">${item.description || item.short_code}</div>
        <img src="${window.location.origin}${item.image_url}" alt="QR" />
        <div class="code">api.sappt.co.kr/api/qr/${item.short_code}</div>
      </div>
      <script>window.onload=()=>{window.print();}<\/script>
      </body></html>
    `);
    win.document.close();
  };

  const columns = [
    {
      title: 'Short Code',
      dataIndex: 'short_code',
      width: 110,
      render: (v: string) => (
        <Text code style={{ fontSize: 13, letterSpacing: 1 }}>{v}</Text>
      ),
    },
    {
      title: '유형',
      dataIndex: 'target_type',
      width: 90,
      render: (v: string) => {
        const meta = TARGET_TYPE_LABELS[v] || { label: v, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: '대상 ID',
      dataIndex: 'target_id',
      width: 80,
    },
    {
      title: '설명',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Redirect URL',
      dataIndex: 'redirect_url',
      ellipsis: true,
      render: (v: string) => (
        <a href={v} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{v}</a>
      ),
    },
    {
      title: '스캔수',
      dataIndex: 'scan_count',
      width: 80,
      sorter: (a: QRItem, b: QRItem) => a.scan_count - b.scan_count,
      render: (v: number) => (
        <Badge count={v} showZero style={{ backgroundColor: v > 0 ? brand.primary : gray[400] }} />
      ),
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      width: 70,
      render: (v: boolean) => <Tag color={v ? 'success' : 'default'}>{v ? '활성' : '비활성'}</Tag>,
    },
    {
      title: '등록일',
      dataIndex: 'created_at',
      width: 110,
      render: (v: string) => v ? new Date(v).toLocaleDateString('ko-KR') : '-',
    },
    {
      title: '작업',
      width: 170,
      render: (_: any, record: QRItem) => (
        <Space size={4}>
          <Tooltip title="QR 이미지">
            <Button size="small" icon={<QrcodeOutlined />} onClick={() => openImage(record)} />
          </Tooltip>
          <Tooltip title="다운로드">
            <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)} />
          </Tooltip>
          <Tooltip title="인쇄">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
          </Tooltip>
          <Tooltip title="스캔이력">
            <Button size="small" icon={<BarChartOutlined />} onClick={() => openLogs(record)} />
          </Tooltip>
          <Tooltip title="편집">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      {/* 통계 카드 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: 10, borderColor: '#e0e7ff' }}>
              <Statistic title="전체 QR" value={stats.total} prefix={<QrcodeOutlined />}
                         valueStyle={{ color: brand.primary }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: 10, borderColor: '#d1fae5' }}>
              <Statistic title="활성 QR" value={stats.active}
                         valueStyle={{ color: '#059669' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: 10, borderColor: '#fef3c7' }}>
              <Statistic title="누적 스캔" value={stats.total_scans}
                         valueStyle={{ color: '#d97706' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: 10, borderColor: '#fce7f3' }}>
              <Statistic title="최근 7일 스캔" value={stats.recent_scans_7d}
                         valueStyle={{ color: '#db2777' }} />
            </Card>
          </Col>
        </Row>
      )}

      {/* 필터 + 새로고침 */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0, color: gray[800] }}>
          <QrcodeOutlined style={{ marginRight: 6, color: brand.primary }} />
          QR 코드 관리
        </Title>
        <Select
          allowClear placeholder="유형 필터"
          style={{ width: 130 }}
          value={filterType}
          onChange={(v) => { setFilterType(v); setPage(1); }}
          options={Object.entries(TARGET_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v.label }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchStats(); }}>
          새로고침
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        size="small"
        pagination={{
          current: page, total, pageSize: 20,
          onChange: (p) => setPage(p),
          showTotal: (t) => `총 ${t}건`,
          showSizeChanger: false,
        }}
        locale={{ emptyText: <EmptyState title="등록된 QR코드가 없습니다" size="sm" /> }}
        style={{ borderRadius: 10, overflow: 'hidden' }}
      />

      {/* QR 이미지 모달 */}
      <Modal
        title={`QR 코드 — ${imageItem?.short_code}`}
        open={imageVisible}
        onCancel={() => setImageVisible(false)}
        footer={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={() => imageItem && handleDownload(imageItem)}>
              다운로드
            </Button>
            <Button icon={<PrinterOutlined />} type="primary" onClick={() => imageItem && handlePrint(imageItem)}>
              인쇄 (포장 표지판)
            </Button>
          </Space>
        }
        width={modalWidth.xs}
        centered
      >
        {imageItem && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <img
              src={imageItem.image_url}
              alt="QR"
              style={{ width: 200, height: 200, border: '1px solid #e2e8f0', borderRadius: 8 }}
            />
            <div style={{ marginTop: 10 }}>
              <Text code style={{ fontSize: 13 }}>{imageItem.short_code}</Text>
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                api.sappt.co.kr/api/qr/{imageItem.short_code}
              </Text>
            </div>
            <div style={{ marginTop: 6 }}>
              <Tag color={TARGET_TYPE_LABELS[imageItem.target_type]?.color}>
                {TARGET_TYPE_LABELS[imageItem.target_type]?.label}
              </Tag>
              <Text style={{ fontSize: 12 }}>{imageItem.description}</Text>
            </div>
          </div>
        )}
      </Modal>

      {/* redirect URL 편집 모달 */}
      <Modal
        title={`QR 편집 — ${editItem?.short_code}`}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={handleEditSave}
        okText="저장"
        cancelText="취소"
        width={modalWidth.sm}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="redirect_url" label="Redirect URL"
            rules={[{ required: true, message: 'URL을 입력하세요' }]}>
            <Input placeholder="https://lab.sappt.co.kr/..." />
          </Form.Item>
          <Form.Item name="description" label="설명">
            <Input placeholder="설명 (선택)" />
          </Form.Item>
          <Form.Item name="is_active" label="활성" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
        <div style={{ background: gray[50], borderRadius: 8, padding: '8px 12px', marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            QR 이미지를 재인쇄하지 않아도 이 URL로 redirect 목적지를 변경할 수 있습니다.
          </Text>
        </div>
      </Modal>

      {/* 스캔 이력 모달 */}
      <Modal
        title={
          <Space>
            <BarChartOutlined style={{ color: brand.primary }} />
            <span>스캔 이력 — <Text code>{logItem?.short_code}</Text></span>
            <Tag color="blue">총 {logTotal}회</Tag>
          </Space>
        }
        open={logVisible}
        onCancel={() => setLogVisible(false)}
        footer={<Button onClick={() => setLogVisible(false)}>닫기</Button>}
        width={modalWidth.md}
        centered
        styles={{ body: { maxHeight: 480, overflowY: 'auto', padding: '8px 0' } }}
      >
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: gray[400] }}>
            스캔 이력이 없습니다.
          </div>
        ) : (
          <List
            size="small"
            dataSource={logs}
            renderItem={(log: any) => (
              <List.Item style={{ padding: '8px 16px' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, fontWeight: 600 }}>
                      {log.scanned_at ? new Date(log.scanned_at).toLocaleString('ko-KR') : '-'}
                    </Text>
                    <Tag style={{ fontSize: 11, margin: 0 }}>{log.ip_address}</Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                    {log.user_agent?.substring(0, 90)}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
}
